import { InsuranceCoverage } from "Store/advice/interfaces";
import {
  CoverageNeed,
  ProductCoverageName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";

import { VALUE_TYPE } from "Components/NumberValue";
import { Description } from "Features/CheckPrice/Products/Advice/adviceStyled";
import { formatToLocaleString } from "Formatting/formatNumber";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { isDisplayedAndNotChecked } from "Store/akaAdvice/interfaces/getters";
import { getCorrectAkaAdviceValues } from "Store/akaAdvice/mappers/mapToFormValues/utils";
import React from "react";
import { checkShouldPurchaseFromAdvisor } from "../../../utils";
import { isRecommendedPensionRestrictedByMaxProductPayment } from "../disabilityCardUtils";

interface Props {
  need: CoverageNeed;
  adviceTexts: any;
  lang: string;
}

export const DisabilityAdviceDescription = ({
  need,
  adviceTexts,
  lang,
}: Props): React.ReactElement => {
  const isAdvisor = useIsAdvisorSelector();
  const { recommendedCoverage } = need;
  const { desc, adviceUnavailableMessages } = adviceTexts;
  const displayedAndNotChecked = isDisplayedAndNotChecked(
    need,
    ProductCoverageName.DISABILITY_FLEX_PENSION,
  );

  if (displayedAndNotChecked) {
    return (
      <Description fw={400} size="14px" className="AdviceTextsReason">
        {adviceUnavailableMessages.disabilityIncome[lang]}
      </Description>
    );
  }

  const recommendedInsuranceSum = getCorrectAkaAdviceValues(
    need,
    ProductCoverageName.DISABILITY_FLEX_PENSION,
    isAdvisor,
  ).adviceValue;

  const shouldPurchaseFromAdvisor = checkShouldPurchaseFromAdvisor(
    isAdvisor,
    need,
    ProductCoverageName.DISABILITY_FLEX_PENSION,
  );

  if (isAdvisor && isRecommendedPensionRestrictedByMaxProductPayment(need)) {
    return (
      <Description fw={400} size="14px">
        {`${desc.part1.weAdvice[lang]}${formatToLocaleString(
          recommendedInsuranceSum,
        )} ${VALUE_TYPE.NOK_YEAR}. ${desc.part1.maximumAmount[lang]}`}
      </Description>
    );
  }

  const weAdvice = shouldPurchaseFromAdvisor
    ? desc.part1[lang]
    : `${desc.part1.weAdvice[lang]}${formatToLocaleString(
        recommendedInsuranceSum,
      )} ${VALUE_TYPE.NOK_YEAR}${desc.part1.whichCovers[lang]}`;

  let percent = "";
  if (recommendedCoverage === InsuranceCoverage.TOP) {
    percent = desc.part2.top[lang];
  } else if (recommendedCoverage === InsuranceCoverage.PLUS) {
    percent = desc.part2.plus[lang];
  } else if (recommendedCoverage === InsuranceCoverage.BASIC) {
    percent = desc.part2.basic[lang];
  }

  const ofIncome = desc.part3[lang];

  let becauseOfYourEconomicalSituation = "";
  if (recommendedCoverage === InsuranceCoverage.BASIC) {
    becauseOfYourEconomicalSituation = desc.part4.basic[lang];
  } else if (recommendedCoverage === InsuranceCoverage.PLUS) {
    becauseOfYourEconomicalSituation = desc.part4.plus[lang];
  } else if (recommendedCoverage === InsuranceCoverage.TOP) {
    becauseOfYourEconomicalSituation = desc.part4.top[lang];
  }
  return (
    <Description fw={400} size="14px" className="AdviceTextsReason">
      {`${weAdvice}${percent}${ofIncome}${becauseOfYourEconomicalSituation}`}
    </Description>
  );
};
